import { useState } from "react";
import PageLayout from "@/components/PageLayout";
import { CheckCircle2 } from "lucide-react";

export default function FormPage({
    eyebrow,
    title,
    tagline,
    description,
    heroImage,
    accentColor = "purple",
    fields,
    submitLabel,
    onSubmit,
    successTitle,
    successMessage,
    testIdPrefix,
}) {
    const [values, setValues] = useState(
        Object.fromEntries(fields.map((f) => [f.name, f.defaultValue || ""]))
    );
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (name, value) => setValues((v) => ({ ...v, [name]: value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await onSubmit(values);
            setDone(true);
        } catch (err) {
            setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const accentMap = {
        purple: "bg-brand-purple",
        lime: "bg-brand-lime",
        blue: "bg-brand-blue",
        red: "bg-brand-red",
    };

    return (
        <PageLayout>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-12 items-start">
                <div data-testid={`${testIdPrefix}-intro`}>
                    <div className={`inline-block text-xs tracking-[0.35em] uppercase text-white px-3 py-1 rounded-full ${accentMap[accentColor]}`}>{eyebrow}</div>
                    <h1 className="mt-5 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05]">{title}</h1>
                    {tagline && <div className="font-accent text-3xl text-brand-purple mt-2 -rotate-2">{tagline}</div>}
                    <p className="mt-6 text-stone-600 leading-relaxed text-lg">{description}</p>
                    {heroImage && (
                        <div className="mt-8 rounded-3xl overflow-hidden border-4 border-brand-cream shadow-xl bg-brand-cream">
                            <img src={heroImage} alt="" className="w-full h-auto block" />
                        </div>
                    )}
                </div>

                <div className="bg-brand-cream rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-stone-100">
                    {done ? (
                        <div className="text-center py-10" data-testid={`${testIdPrefix}-success`}>
                            <CheckCircle2 size={56} className="mx-auto text-brand-lime" />
                            <h2 className="font-serif text-3xl mt-4">{successTitle}</h2>
                            <p className="text-stone-600 mt-3">{successMessage}</p>
                            <button
                                onClick={() => { setDone(false); setValues(Object.fromEntries(fields.map((f) => [f.name, ""]))); }}
                                data-testid={`${testIdPrefix}-submit-another`}
                                className="mt-6 px-6 py-2 rounded-full border border-stone-300 text-sm hover:bg-stone-100"
                            >
                                Submit another
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5" data-testid={`${testIdPrefix}-form`}>
                            {fields.map((f) => (
                                <div key={f.name}>
                                    <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{f.label}{f.required && <span className="text-brand-red"> *</span>}</label>
                                    {f.type === "textarea" ? (
                                        <textarea
                                            data-testid={`${testIdPrefix}-input-${f.name}`}
                                            required={f.required}
                                            rows={f.rows || 5}
                                            value={values[f.name]}
                                            onChange={(e) => handleChange(f.name, e.target.value)}
                                            placeholder={f.placeholder}
                                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                                        />
                                    ) : f.type === "select" ? (
                                        <select
                                            data-testid={`${testIdPrefix}-input-${f.name}`}
                                            required={f.required}
                                            value={values[f.name]}
                                            onChange={(e) => handleChange(f.name, e.target.value)}
                                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                                        >
                                            <option value="">Select...</option>
                                            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    ) : (
                                        <input
                                            data-testid={`${testIdPrefix}-input-${f.name}`}
                                            type={f.type || "text"}
                                            required={f.required}
                                            value={values[f.name]}
                                            onChange={(e) => handleChange(f.name, e.target.value)}
                                            placeholder={f.placeholder}
                                            className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                                        />
                                    )}
                                </div>
                            ))}
                            {error && <div className="text-sm text-brand-red" data-testid={`${testIdPrefix}-error`}>{error}</div>}
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid={`${testIdPrefix}-submit`}
                                className={`w-full px-7 py-4 rounded-full text-white font-semibold tracking-wide ${accentMap[accentColor]} hover:opacity-90 transition disabled:opacity-50`}
                            >
                                {loading ? "Submitting..." : submitLabel}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </PageLayout>
    );
}
